// One home for every repair/cleanup tool (Henry, 2026-08-30: "they are in so
// many places — let's put them all in one spot"). Rendered on Settings →
// Super Admin. Each tool previews what it would change and asks before
// touching anything; every one is safe to re-run.
//
// The handlers moved here from the Task Templates toolbar unchanged, except
// Backfill Active Deals now loads the task template itself instead of
// borrowing the template page's state.
import { useState } from "react";
import {
  getTaskTemplate,
  getAlldealforAdmin,
  getUsers,
  backfillActiveDeals,
  backfillBuyerFileRequestsToPipeline,
  archiveConvertedRecords,
} from "../../utils/firebasefunctions";
import { previewDealBackfill } from "../../utils/taskSections";
import {
  backfillBuyerFolders,
  backfillListingFolders,
} from "../../utils/driveClient";
import DuplicateLeadsModal from "../task-template/DuplicateLeadsModal";
import { useActionDialog } from "../ActionDialog";

export default function RepairToolsPanel() {
  const [busy, setBusy] = useState("");
  const [showDupModal, setShowDupModal] = useState(false);
  const { confirm, notify, dialog } = useActionDialog();

  const backfillActive = async () => {
    setBusy("backfill");
    try {
      const [deals, users, template] = await Promise.all([
        getAlldealforAdmin("deals"),
        getUsers(),
        getTaskTemplate(),
      ]);
      const tasks = template || {};
      // Say what will actually change, not how many deals were looked at.
      const { active, needing, missing } = previewDealBackfill(deals || [], tasks);
      if (needing === 0) {
        await notify({
          title: "Nothing to add",
          body: `All ${active} active deal(s) already have their checklist.`,
        });
        return;
      }
      const ok = await confirm({
        title: "Backfill active deals",
        body: `${needing} of ${active} active deal(s) are missing checklist items, ${missing} task(s) in total. Add them now?`,
        note: "Nothing is deleted or unticked, finished and archived deals are skipped, and it's safe to re-run.",
        confirmLabel: "Add them",
      });
      if (!ok) return;
      const usersById = (users || []).reduce((m, u) => { m[u.id] = u; return m; }, {});
      const res = await backfillActiveDeals(deals || [], tasks, usersById, new Date().toISOString());
      await notify({
        title: "Backfill complete",
        body: `Updated ${res.updated} deal(s), skipped ${res.skipped}.`,
      });
    } catch (e) {
      console.log("Backfill error", e);
      await notify({ title: "Backfill failed", body: "See the browser console for details." });
    } finally {
      setBusy("");
    }
  };

  // One-time migration for the "buyer file-requests live in the Buyers
  // Pipeline" change: flags existing buyer file-request leads as File
  // Requested. The board already hides those cards; this lights the pipeline.
  const migrateBuyerFileRequests = async () => {
    const ok = await confirm({
      title: "Migrate buyer file-requests",
      body: "Move existing buyer file-requests off the Deals Board into the Buyers Pipeline? This flags their leads as File Requested.",
      note: "Safe to re-run.",
      confirmLabel: "Move them",
    });
    if (!ok) return;
    setBusy("migrate");
    try {
      const res = await backfillBuyerFileRequestsToPipeline();
      await notify({
        title: "Migration complete",
        body: `Flagged ${res.flagged} buyer lead(s), skipped ${res.skipped}.`,
      });
    } catch (e) {
      console.log("Buyer file-request migration error", e);
      await notify({ title: "Migration failed", body: "See the browser console for details." });
    } finally {
      setBusy("");
    }
  };

  // One-time repair for buyer Drive folders created before buyers got their
  // own home. Dry run first so the list shows before anything moves.
  const repairBuyerFolders = async () => {
    setBusy("repair");
    try {
      const preview = await backfillBuyerFolders("dryrun");
      if (!preview.count) {
        await notify({
          title: "Nothing to repair",
          body: "Every buyer folder is already filed under its agent.",
        });
        return;
      }
      const items = preview.candidates.map((c) => `• ${c.wantedName} → ${c.agentName}`);
      const newFolders = preview.newAgentFolders || [];
      const newLine = newFolders.length
        ? ` New agent folder(s) will be created for: ${newFolders.join(", ")}.`
        : "";
      const ok = await confirm({
        title: "Repair buyer folders",
        body: `Move ${preview.count} buyer folder(s) into their agent's folder?${newLine}`,
        items,
        note: "Nothing is deleted, folders you renamed yourself keep their names, and it's safe to re-run.",
        confirmLabel: "Move them",
      });
      if (!ok) return;
      const res = await backfillBuyerFolders("execute");
      const failed = (res.results || []).filter((r) => !r.ok).length;
      await notify({
        title: "Repair complete",
        body: `Moved ${res.moved}, renamed ${res.renamed}.${failed ? ` ${failed} failed — see the browser console.` : ""}`,
      });
      if (failed) console.log("Buyer folder repair failures", res.results);
    } catch (e) {
      console.log("Buyer folder repair error", e);
      await notify({ title: "Repair failed", body: e?.message || "See the browser console for details." });
    } finally {
      setBusy("");
    }
  };

  // Files listings into "<Agent> - SELLERS" folders — moves existing folders
  // AND creates folders (Net Sheet included) for listings that never got one.
  const sweepListingFolders = async () => {
    setBusy("sweep");
    try {
      const preview = await backfillListingFolders("dryrun");
      const toCreate = preview.createCandidates || [];
      if (!preview.count && !toCreate.length) {
        await notify({
          title: "Nothing to sweep",
          body: "Every listing folder is already filed under its agent.",
        });
        return;
      }
      const items = [
        ...preview.candidates.map(
          (c) => `• ${c.propertyaddress || c.wantedName} → ${c.agentName} - SELLERS`,
        ),
        ...toCreate.map(
          (c) =>
            `• ${c.propertyaddress || c.wantedName} → new folder${c.agentName ? ` under ${c.agentName} - SELLERS` : ""}`,
        ),
      ];
      const newFolders = preview.newAgentFolders || [];
      const newLine = newFolders.length
        ? ` New agent folder(s) will be created for: ${newFolders.join(", ")}.`
        : "";
      const createLine = toCreate.length
        ? ` ${toCreate.length} listing(s) have no Drive folder yet — one will be created for each, with its Net Sheet filled in.`
        : "";
      const adopted = preview.adoptedAgentFolders || [];
      const adoptLine = adopted.length
        ? ` Existing folder(s) will be renamed: ${adopted
            .map((a) => `"${a.from}" to "${a.to}"`)
            .join(", ")}.`
        : "";
      const ok = await confirm({
        title: "Sweep listing folders",
        body: `Move ${preview.count} listing folder(s) into their agent's SELLERS folder?${createLine}${newLine}${adoptLine}`,
        items,
        note: "Nothing is deleted, deal folders you renamed yourself keep their names, and it's safe to re-run.",
        confirmLabel: "Run the Sweep",
      });
      if (!ok) return;
      const res = await backfillListingFolders("execute");
      const failed = (res.results || []).filter((r) => !r.ok).length;
      await notify({
        title: "Sweep complete",
        body: `Moved ${res.moved}, renamed ${res.renamed}, created ${res.created || 0}.${failed ? ` ${failed} failed — see the browser console.` : ""}`,
      });
      if (failed) console.log("Listing folder sweep failures", res.results);
    } catch (e) {
      console.log("Listing folder sweep error", e);
      await notify({ title: "Sweep failed", body: e?.message || "See the browser console for details." });
    } finally {
      setBusy("");
    }
  };

  // Leads/appointments that converted but were never archived — status flips
  // only, nothing deleted, dry-run list shown first.
  const archiveConverted = async () => {
    setBusy("archive");
    try {
      const plan = await archiveConvertedRecords({ dryRun: true });
      const total =
        plan.listingLeadsToArchive.length +
        plan.buyerLeadsToArchive.length +
        plan.appointmentsToWin.length;
      if (total === 0) {
        await notify({
          title: "Nothing to clean up",
          body: "No converted leads are lingering.",
        });
        return;
      }
      const items = [
        ...plan.listingLeadsToArchive.map((x) => `• Listing lead: ${x.label}`),
        ...plan.buyerLeadsToArchive.map((x) => `• Buyer lead: ${x.label}`),
        ...plan.appointmentsToWin.map((x) => `• Appointment → Won: ${x.label}`),
      ];
      const ok = await confirm({
        title: "Archive converted leads",
        body: `Archive ${total} record(s) whose property is already on the board?`,
        items,
        note: "Status flips only, nothing is deleted. Safe to re-run.",
        confirmLabel: "Archive them",
      });
      if (!ok) return;
      const res = await archiveConvertedRecords();
      await notify({
        title: "Cleanup complete",
        body:
          `Archived ${res.listingLeadsToArchive.length} listing lead(s), ` +
          `${res.buyerLeadsToArchive.length} buyer lead(s), marked ` +
          `${res.appointmentsToWin.length} appointment(s) won` +
          (res.failed ? `. ${res.failed} write(s) FAILED, re-run to retry.` : "."),
      });
    } catch (e) {
      console.log("Archive converted leads error", e);
      await notify({ title: "Cleanup failed", body: "See the browser console for details." });
    } finally {
      setBusy("");
    }
  };

  const TOOLS = [
    {
      key: "sweep",
      title: "Sweep Listing Folders",
      desc: "Files every live listing into its agent's SELLERS folder in Drive — creating folders (Net Sheet included) for listings that never got one.",
      run: sweepListingFolders,
    },
    {
      key: "repair",
      title: "Repair Buyer Folders",
      desc: "Files stray buyer folders under their agent's BUYERS folder and names them after the client.",
      run: repairBuyerFolders,
    },
    {
      key: "backfill",
      title: "Backfill Active Deals",
      desc: "Adds any missing checklist items from the Task Template onto active deals.",
      run: backfillActive,
    },
    {
      key: "archive",
      title: "Archive Converted Leads",
      desc: "Archives pipeline leads whose property is already on the Deals Board, so they stop lingering.",
      run: archiveConverted,
    },
    {
      key: "migrate",
      title: "Migrate Buyer File-Requests",
      desc: "Flags old buyer file-requests so they show in the Buyers Pipeline instead of the Deals Board.",
      run: migrateBuyerFileRequests,
    },
    {
      key: "duplicates",
      title: "Merge Duplicate Leads",
      desc: "Finds duplicate lead cards across both pipelines and lets you pick which one survives.",
      run: () => setShowDupModal(true),
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <h3 className="text-sm font-semibold text-slate-800">Repair Tools</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-3">
        Every tool shows you exactly what it would change and asks first.
        Nothing here deletes anything, and each one is safe to run again.
      </p>
      <div className="divide-y divide-gray-100">
        {TOOLS.map((t) => (
          <div key={t.key} className="py-3 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-800">{t.title}</div>
              <p className="text-xs text-slate-500 mt-0.5">{t.desc}</p>
            </div>
            <button
              type="button"
              onClick={t.run}
              disabled={!!busy}
              className="shrink-0 text-sm font-semibold text-[#1E293B] bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition disabled:opacity-60"
            >
              {busy === t.key ? "Working…" : "Run"}
            </button>
          </div>
        ))}
      </div>
      {showDupModal && (
        <DuplicateLeadsModal onClose={() => setShowDupModal(false)} />
      )}
      {dialog}
    </div>
  );
}
